/**
 * Notification Types for API Retry System
 *
 * Core notification event types used for internal coordination between the
 * Request Coordinator, Retry Engine, Circuit Breaker, and Deduplication Manager.
 *
 * (The richer user-facing retry notification system was removed; only these
 * lightweight coordination types remain.)
 *
 * @module types/api/notification
 */

// =============================================================================
// CORE NOTIFICATION TYPES (Internal Coordination)
// =============================================================================

/**
 * Core notification type enumeration for internal request coordination.
 *
 * These types are used for internal event handling between the Request Coordinator,
 * Retry Engine, Circuit Breaker, and Deduplication Manager.
 *
 * @example
 * ```typescript
 * import { CoreNotificationType } from '../types/api';
 *
 * if (event.type === CoreNotificationType.RETRY_STARTED) {
 *   console.log('Retry started');
 * }
 * ```
 */
export enum CoreNotificationType {
  /** A retry sequence has started */
  RETRY_STARTED = 'retry_started',
  /** A single retry attempt is being made */
  RETRY_ATTEMPT = 'retry_attempt',
  /** A retry attempt succeeded */
  RETRY_SUCCESS = 'retry_success',
  /** All retry attempts have been exhausted */
  RETRY_EXHAUSTED = 'retry_exhausted',
  /** Circuit breaker has opened (blocking requests) */
  CIRCUIT_BREAKER_OPENED = 'circuit_breaker_opened',
  /** Circuit breaker has closed (allowing requests) */
  CIRCUIT_BREAKER_CLOSED = 'circuit_breaker_closed',
  /** Authentication refresh is required */
  AUTH_REFRESH_REQUIRED = 'auth_refresh_required',
  /** A request was cancelled */
  REQUEST_CANCELLED = 'request_cancelled',
  /** Performance warning threshold exceeded */
  PERFORMANCE_WARNING = 'performance_warning',
  /** A request was deduplicated */
  REQUEST_DEDUPLICATED = 'request_deduplicated',
  /** Deduplication cache hit */
  DEDUPLICATION_CACHE_HIT = 'deduplication_cache_hit',
  /** Deduplication cache miss */
  DEDUPLICATION_CACHE_MISS = 'deduplication_cache_miss',
}

/**
 * Core notification event for internal request coordination.
 *
 * This is a simplified event interface used for internal communication
 * between the Request Coordinator, Retry Engine, Circuit Breaker, and
 * Deduplication Manager. For rich notification events with detailed data,
 * use the specific event types (RetryStartedEvent, RetryAttemptEvent, etc.).
 *
 * @example
 * ```typescript
 * const event: CoreNotificationEvent = {
 *   type: CoreNotificationType.RETRY_STARTED,
 *   requestId: 'req_123',
 *   message: 'Starting retry sequence',
 *   timestamp: dayjs().valueOf(),
 *   data: {
 *     endpoint: '/api/users',
 *     maxAttempts: 3,
 *   },
 * };
 * ```
 */
export interface CoreNotificationEvent {
  /** Type of notification event */
  type: CoreNotificationType;
  /** Request ID associated with this event */
  requestId: string;
  /** Human-readable message describing the event */
  message: string;
  /** Timestamp when the event occurred */
  timestamp: number;
  /** Additional data associated with the event */
  data?: Record<string, unknown>;
}
